import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, Section, Button } from './index';

/**
 * Contact and Subscribe section with newsletter signup and social media links
 */
const Contact = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const socialLinks = [
    {
      name: 'Twitter',
      icon: '🐦',
      url: '#',
      color: 'hover:text-blue-500'
    },
    {
      name: 'Telegram',
      icon: '📱',
      url: '#',
      color: 'hover:text-blue-600'
    },
    {
      name: 'Discord',
      icon: '💬',
      url: '#',
      color: 'hover:text-indigo-600'
    },
    {
      name: 'Medium',
      icon: '📝',
      url: '#',
      color: 'hover:text-green-600'
    },
    {
      name: 'GitHub',
      icon: '💻',
      url: '#',
      color: 'hover:text-gray-800'
    },
    {
      name: 'LinkedIn',
      icon: '💼',
      url: '#',
      color: 'hover:text-blue-700'
    }
  ];
  
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
      setEmail('');
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setIsSubscribed(false);
      }, 3000);
    }, 1000);
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };
  
  return (
    <Section id="contact" background="pattern" className="relative">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-10 left-10 w-20 h-20 bg-coffee-200 rounded-full opacity-10 animate-float"></div>
        <div className="absolute bottom-20 right-20 w-16 h-16 bg-coffee-300 rounded-full opacity-15 animate-bounce-slow"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-coffee-400 rounded-full opacity-10 animate-float"></div>
      </div>
      
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className="relative z-10"
      >
        {/* Section Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
            <span className="gradient-text">Stay Connected</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-coffee-600 to-coffee-800 mx-auto mb-8"></div>
          <p className="text-lg text-coffee-600 max-w-2xl mx-auto">
            Join our community and be the first to know about BigCash Coffee Token updates, 
            partnerships, and exclusive coffee rewards.
          </p>
        </motion.div>
        
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Newsletter Subscription */}
          <motion.div variants={itemVariants}>
            <Card className="h-full">
              <div className="text-center mb-8">
                <div className="text-4xl mb-4">📧</div>
                <h3 className="text-2xl font-bold text-coffee-800 mb-4">
                  Newsletter Subscription
                </h3>
                <p className="text-coffee-600">
                  Get exclusive updates, coffee tips, and token news delivered to your inbox.
                </p>
              </div>
              
              <form onSubmit={handleSubscribe} className="space-y-4">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 border border-coffee-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-transparent transition-all duration-300"
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isLoading || isSubscribed}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Subscribing...
                    </span>
                  ) : isSubscribed ? (
                    <span className="flex items-center justify-center">
                      <span className="mr-2">✅</span>
                      Successfully Subscribed!
                    </span>
                  ) : (
                    'Subscribe to Newsletter'
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-coffee-500">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            </Card>
          </motion.div>
          
          {/* Social Media & Community */}
          <motion.div variants={itemVariants}>
            <Card className="h-full">
              <div className="text-center mb-8">
                <div className="text-4xl mb-4">🌐</div>
                <h3 className="text-2xl font-bold text-coffee-800 mb-4">
                  Join Our Community
                </h3>
                <p className="text-coffee-600">
                  Connect with fellow coffee lovers and stay updated on all BigCash developments.
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-8">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex flex-col items-center p-4 rounded-lg border border-coffee-200 hover:border-coffee-400 transition-all duration-300 ${social.color} group`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="text-2xl mb-2 group-hover:scale-110 transition-transform duration-300">
                      {social.icon}
                    </span>
                    <span className="text-sm font-medium text-coffee-700">
                      {social.name}
                    </span>
                  </motion.a>
                ))}
              </div>
              
              {/* Community Stats */}
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-coffee-50 rounded-lg">
                  <div className="text-2xl font-bold text-coffee-800">4.5K+</div>
                  <div className="text-sm text-coffee-600">Token Holders</div>
                </div>
                <div className="p-3 bg-coffee-50 rounded-lg">
                  <div className="text-2xl font-bold text-coffee-800">1.2K+</div>
                  <div className="text-sm text-coffee-600">Community Members</div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
        
        {/* Contact Information */}
        <motion.div variants={itemVariants} className="mt-16">
          <Card variant="gradient" className="text-center max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-coffee-800 mb-6">
              Get in Touch
            </h3>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <div className="text-3xl mb-3">📧</div>
                <h4 className="font-semibold text-coffee-800 mb-2">Email</h4>
                <p className="text-coffee-600">hello@bigcash-coffee.com</p>
              </div>
              
              <div>
                <div className="text-3xl mb-3">💬</div>
                <h4 className="font-semibold text-coffee-800 mb-2">Support</h4>
                <p className="text-coffee-600">support@bigcash-coffee.com</p>
              </div>
              
              <div>
                <div className="text-3xl mb-3">🤝</div>
                <h4 className="font-semibold text-coffee-800 mb-2">Partnerships</h4>
                <p className="text-coffee-600">partners@bigcash-coffee.com</p>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-coffee-200">
              <p className="text-coffee-600">
                <span className="font-semibold">Business Hours:</span> Monday - Friday, 9:00 AM - 6:00 PM (GMT+8)
              </p>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </Section>
  );
};

export default Contact;